import { EventHandler } from '../event-handler'

/**
 * Class representing a mapper for event names to pages.
 * This class extends EventHandler and provides functionality to map URL patterns to page names.
 * @extends EventHandler
 */
export class EventNamePageMapper extends EventHandler {
  /**
   * Array of mappings between URL patterns and page names.
   * @private
   * @type {Array<{ pattern: RegExp, page: string }>}
   */
  _patternToPage = []

  /**
   * Adds a new mapping between a URL pattern and a page name.
   * @param {RegExp | string} pattern - The URL pattern to match. Can be a RegExp object or a string.
   * @param {string} page - The page name associated with the pattern.
   */
  addMapping(pattern, page) {
    if (pattern instanceof RegExp) {
      this._patternToPage.push({ pattern, page })
    } else {
      this._patternToPage.push({ pattern: new RegExp(pattern), page })
    }
  }

  /**
   * Gets the page name for a given URL based on the stored mappings.
   * @param {string} url - The URL to find a matching page for.
   * @returns {string} The page name associated with the matching pattern.
   * @throws {Error} If no matching pattern is found for the given URL.
   */
  getPage(url) {
    const match = this._patternToPage.find((it) => it.pattern.test(url))
    if (!match) {
      throw new Error(`No matching pattern found for URL: ${url}`)
    }
    return match.page
  }

  /**
   * Gets the current URL from the browser's location.
   * @returns {string} The current URL pathname.
   */
  getCurrentUrl() {
    return window.location.pathname
  }

  /**
   * Gets the page name for the current URL.
   * @returns {string} The page name associated with the current URL.
   */
  getCurrentPage() {
    return this.getPage(this.getCurrentUrl())
  }
}
